/**
 * StatsPanel — shared component that renders lifetime stats for a user.
 *
 * Used by both /(app)/settings/stats (own stats) and /(app)/u/[username]
 * (public profile view). Both screens pass the same PublicProfile shape
 * so neither forks the rendering logic.
 */

import React from 'react'
import { Platform, View, Text, TouchableOpacity } from 'react-native'
import { formatAverageTurnValue } from '../../lib/turnTimes'
import { HelpButton } from '../ui/HelpButton'
import { UnrankedGamesHelpModal } from './UnrankedGamesHelpModal'

export interface UserStats {
  gamesPlayed: number
  wins: number
  winPct: number
  /** Lifetime completed turns and mean turn length in seconds. Absent from older servers. */
  turnCount?: number
  averageTurnSeconds?: number | null
}

/** Unranked games (a human alone with bots) and their turns. Absent from older servers. */
export interface UnrankedStats {
  gamesPlayed: number
  wins: number
  losses: number
  winPct: number
  turnCount: number
  averageTurnSeconds: number | null
}

/** The stats to show: ranked only, or ranked + unranked combined by the server. */
export function displayedStats(stats: UserStats, combined: UserStats | undefined, includeUnranked: boolean): UserStats {
  return includeUnranked && combined ? combined : stats
}

function formatPct(n: number): string {
  if (n <= 0) return '0%'
  return `${Math.round(n * 1000) / 10}%`
}

/** Gap between grid boxes, in points. */
const GRID_GAP = 8

/** Width of a box covering `span` columns of a three-column grid `gridWidth` wide. */
export function statColumnWidth(gridWidth: number, span: 1 | 2): number {
  const column = (gridWidth - 2 * GRID_GAP) / 3
  return span * column + (span - 1) * GRID_GAP
}

const GridWidth = React.createContext<number | null>(null)

const VALUE_FIT = Platform.OS === 'web' ? {} : { numberOfLines: 1, adjustsFontSizeToFit: true, minimumFontScale: 0.5 }

function StatBlock({
  label,
  value,
  span = 1,
  secondary = false,
}: {
  label: string
  value: string
  /** Columns of the three-column grid this box covers. */
  span?: 1 | 2
  /** A breakdown under the main totals: outlined and quieter. */
  secondary?: boolean
}) {
  // Explicit widths from the measured grid: flex sizing let a box grow to fit
  // its text, so columns drifted out of line between rows.
  const gridWidth = React.useContext(GridWidth)
  return (
    <View
      className={secondary
        ? 'rounded-md px-4 py-2 gap-0.5 items-start border border-dashed border-olive'
        : 'bg-paper-up rounded-md px-4 py-3 gap-1 items-start'}
      style={gridWidth === null ? { flex: span } : { width: statColumnWidth(gridWidth, span) }}
    >
      <Text className="font-typed text-ink-soft text-[10px] uppercase tracking-widest">
        {label}
      </Text>
      {/* Never truncated: native shrinks the value to fit; web (no font
          fitting, where a line limit would ellipsize "2.9%") wraps instead. */}
      <Text className={secondary ? 'font-stencil text-ink-soft text-lg' : 'font-stencil text-ink text-2xl'} {...VALUE_FIT}>
        {value}
      </Text>
    </View>
  )
}

/** One row of the three-column grid; boxes keep their column width in short rows. */
function StatRow({ children }: { children: React.ReactNode }) {
  return <View className="flex-row" style={{ gap: GRID_GAP }}>{children}</View>
}

interface StatsPanelProps {
  stats: UserStats
  /** Unranked games; with `combinedStats`, enables the "Include unranked games" toggle. */
  unrankedStats?: UnrankedStats
  /** Ranked + unranked totals (games, wins and turns), computed by the server. */
  combinedStats?: UserStats
}

export function StatsPanel({ stats, unrankedStats, combinedStats }: StatsPanelProps) {
  const [gridWidth, setGridWidth] = React.useState<number | null>(null)
  // Lifetime stats are ranked games (2+ human players) by default.
  const [includeUnranked, setIncludeUnranked] = React.useState(false)
  const [helpOpen, setHelpOpen] = React.useState(false)
  const canInclude = !!unrankedStats && !!combinedStats
  const shown = displayedStats(stats, combinedStats, includeUnranked)
  return (
    <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3">
      <View className="flex-row items-center justify-between gap-2">
        <Text className="font-stencil text-ink uppercase text-sm tracking-widest flex-shrink">
          Lifetime Stats
        </Text>
        {canInclude && (
          <View className="flex-row items-center gap-2">
            <TouchableOpacity
              onPress={() => setIncludeUnranked(value => !value)}
              accessibilityRole="switch"
              accessibilityLabel="Include unranked games"
              accessibilityState={{ checked: includeUnranked }}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              className={`flex-row items-center gap-1 rounded-full px-2.5 py-1 border ${includeUnranked ? 'bg-paper-up border-ink' : 'border-olive'}`}
            >
              <Text className={`font-typed text-xs ${includeUnranked ? 'text-ink' : 'text-ink-soft'}`}>Include unranked games</Text>
              {includeUnranked && <Text className="text-ink font-stencil text-[10px]">✓</Text>}
            </TouchableOpacity>
            <HelpButton onPress={() => setHelpOpen(true)} accessibilityLabel="What are unranked games?" size={26} />
          </View>
        )}
      </View>
      <Text className="font-typed text-ink-soft text-xs">
        {includeUnranked
          ? 'Ranked and unranked games combined.'
          : 'Ranked games only.'}
      </Text>
      <GridWidth.Provider value={gridWidth}>
        <View className="gap-3" onLayout={(e) => setGridWidth(e.nativeEvent.layout.width)}>
          <StatRow>
            <StatBlock label="Games" value={shown.gamesPlayed.toString()} />
            <StatBlock label="Wins" value={shown.wins.toString()} />
            <StatBlock label="Win %" value={formatPct(shown.winPct)} />
          </StatRow>
          {shown.turnCount !== undefined && (
            <StatRow>
              <StatBlock label="Turns" value={shown.turnCount.toString()} />
              <StatBlock label="Avg turn time" value={formatAverageTurnValue(shown.averageTurnSeconds)} span={2} />
            </StatRow>
          )}
          {includeUnranked && unrankedStats && (
            // A quieter breakdown so it reads as part of the totals above, not more of them.
            <View className="gap-2 border-t border-olive/40 pt-3">
              <Text className="font-stencil text-ink-soft uppercase text-[10px] tracking-widest">Unranked breakdown</Text>
              <StatRow>
                <StatBlock secondary label="Games" value={unrankedStats.gamesPlayed.toString()} />
                <StatBlock secondary label="Wins" value={unrankedStats.wins.toString()} />
                <StatBlock secondary label="Win %" value={formatPct(unrankedStats.winPct)} />
              </StatRow>
            </View>
          )}
        </View>
      </GridWidth.Provider>
      <UnrankedGamesHelpModal visible={helpOpen} onClose={() => setHelpOpen(false)} />
      {shown.gamesPlayed === 0 && (
        <Text className="font-typed text-ink-soft text-xs italic">
          // no games completed yet — get out there
        </Text>
      )}
    </View>
  )
}
